if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }

      if (this.thumbsHeightObserver) {
        this.thumbsHeightObserver.disconnect();
      } else if (this.thumbsHeightHandler) {
        window.removeEventListener('on:debounced-resize', this.thumbsHeightHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.thumbsPrevBtn = this.querySelector('.media-thumbs-nav__btn[name="prev"]');
      this.thumbsNextBtn = this.querySelector('.media-thumbs-nav__btn[name="next"]');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        if (this.getMediaGroupData().groupType === 'variant') {
          if (this.getMediaGroupData().selectedVariantId) {
            this.setActiveMediaGroup(String(this.getMediaGroupData().selectedVariantId));
          }
        } else {
          this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
        }
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.dataset.layout === 'slider') {
        this.syncThumbsHeight();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Keeps the thumbnail column's max height in sync with the rendered height of the
     * main viewer, so the "slider" layout thumbs never grow taller than the main image.
     */
    syncThumbsHeight() {
      const main = this.querySelector('.media-gallery__main');
      if (!main) return;

      const updateHeight = () => {
        this.style.setProperty('--media-main-height', `${main.offsetHeight}px`);
        this.updateThumbNav();
      };

      updateHeight();

      if ('ResizeObserver' in window) {
        this.thumbsHeightObserver = new ResizeObserver(updateHeight);
        this.thumbsHeightObserver.observe(main);
      } else {
        this.thumbsHeightHandler = updateHeight;
        window.addEventListener('on:debounced-resize', this.thumbsHeightHandler);
      }
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        if (this.getMediaGroupData().groupType === 'variant') {
          if (evt.detail.variant) {
            this.setActiveMediaGroup(String(evt.detail.variant.id));
          }
        } else {
          this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
        }
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        const mediaInfo = {};
        variantMediaData.variantMedia.forEach((media) => {
          mediaInfo[media.mediaId] = media;
        });

        const unassignedItems = [];
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          const info = mediaInfo[parseInt(item.dataset.mediaId, 10)];
          const groupItem = { main: item };
          if (this.thumbs) {
            groupItem.thumb = this.thumbs.querySelector(
              `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
            );
          }

          // A media item can belong to one option value group, or (in variant
          // group mode) to one group per variant it is the featured media of.
          let groupKeys = [];
          if (info) {
            if (variantMediaData.groupType === 'variant') {
              groupKeys = (info.variants || []).map((id) => String(id));
            } else if (info.option) {
              groupKeys = [info.option];
            }
          }

          if (groupKeys.length > 0) {
            groupKeys.forEach((key) => {
              if (!this.mediaGroupMap.groups[key]) {
                this.mediaGroupMap.groups[key] = {
                  name: key,
                  items: []
                };
              }
              this.mediaGroupMap.groups[key].items.push(groupItem);
            });
          } else {
            // Media not associated with any group
            unassignedItems.push(groupItem);
          }
        });

        if (unassignedItems.length > 0) {
          // Move unassigned media to the end of the gallery and include it in every group
          unassignedItems.forEach((groupItem) => {
            this.viewer.appendChild(groupItem.main);
            if (groupItem.thumb) {
              this.thumbs.appendChild(groupItem.thumb);
            }
          });

          Object.keys(this.mediaGroupMap.groups).forEach((groupName) => {
            unassignedItems.forEach((groupItem) => {
              this.mediaGroupMap.groups[groupName].items.push(groupItem);
            });
          });

          this.viewerItems = this.querySelectorAll('.media-viewer__item');
        }

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }

      this.updateThumbNav();
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      if (this.thumbsPrevBtn) {
        this.thumbsPrevBtn.addEventListener('click', () => this.scrollThumbs('prev'));
      }
      if (this.thumbsNextBtn) {
        this.thumbsNextBtn.addEventListener('click', () => this.scrollThumbs('next'));
      }
      if (this.thumbs) {
        this.thumbs.addEventListener('scroll', () => this.updateThumbNav());
      }
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('.media-thumbs__item[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.viewer.querySelector(
        `.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`
      );
      if (!itemToShow) return;

      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }

      this.updateThumbNav();
    }

    /**
     * Scrolls the thumbnail column up or down (desktop vertical layout).
     * @param {'prev'|'next'} direction - Scroll direction.
     */
    scrollThumbs(direction) {
      if (!this.thumbs || !theme.mediaMatches.md) return;

      const thumbItem = this.thumbs.querySelector('.media-thumbs__item:not([style*="display: none"])');
      const amount = thumbItem ? thumbItem.offsetHeight + 5 : this.thumbs.clientHeight * 0.75;

      this.thumbs.scrollBy({
        top: direction === 'next' ? amount : -amount,
        behavior: 'smooth'
      });
    }

    /**
     * Enables/disables thumbnail nav arrows based on scroll position.
     */
    updateThumbNav() {
      if (!this.thumbs || !this.thumbsPrevBtn || !this.thumbsNextBtn) return;

      if (!theme.mediaMatches.md || this.dataset.layout !== 'slider') {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = this.thumbs;
      const maxScroll = scrollHeight - clientHeight;

      this.thumbsPrevBtn.disabled = scrollTop <= 1;
      this.thumbsNextBtn.disabled = maxScroll <= 1 || scrollTop >= maxScroll - 1;
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      if (this.dataset.layout === 'slider' && theme.mediaMatches.md) {
        const scrollPos = this.thumbs.scrollTop;
        const lastVisibleThumbOffset = this.thumbs.clientHeight + scrollPos;
        const thumbOffset = thumb.offsetTop;

        if (thumbOffset + thumb.clientHeight > lastVisibleThumbOffset || thumbOffset < scrollPos) {
          this.thumbs.scrollTo({ top: thumbOffset, behavior: 'smooth' });
        }
      } else {
        const scrollPos = this.thumbs.scrollLeft;
        const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
        const thumbOffset = thumb.offsetLeft;

        if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
          this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
        }
      }

      this.updateThumbNav();
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
